import React from "react";

interface EmailAuditEntry {
  id: number;
  created_at: string;
  email_activity: string;
  email_to_list: string[];
  email_from_list: string[];
  action_done_by: string | null;
  assigned_by: string | null;
  old_assignee: string | null;
  new_assignee: string | null;
  previous_value: string | null;
  new_value: string | null;
}

interface IProps {
  emailAuditTrailData: EmailAuditEntry[];
}

const EmailAuditTrail: React.FC<IProps> = ({ emailAuditTrailData }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

const getActivityType = (activity: string) => {
  const a = activity?.toLowerCase() || "";

  if (a.includes("forwarded")) {
    return {
      icon: "bi-forward-fill",
      iconClass: "atl-icon-forwarded",
      badge: "atl-badge-forwarded",
      badgeText: "FORWARDED",
    };
  } else if (a.includes("disposition")) {
    return {
      icon: "bi-tags-fill",
      iconClass: "atl-icon-disposition",
      badge: "atl-badge-disposition",
      badgeText: "DISPOSITION",
    };
  } else if (a.includes("non-actionable")) {
    return {
      icon: "bi-envelope-slash-fill",
      iconClass: "atl-icon-nonactionable",
      badge: "atl-badge-nonactionable",
      badgeText: "NON-ACTIONABLE",
    };
  } else if (a.includes("assignee")) {
    return {
      icon: "bi-person-fill-gear",
      iconClass: "atl-icon-assigned",
      badge: "atl-badge-assigned",
      badgeText: "ASSIGNED",
    };
  } else if (a.includes("created") || a.includes("initialized")) {
    return {
      icon: "bi-plus-circle-fill",
      iconClass: "atl-icon-created",
      badge: "atl-badge-created",
      badgeText: "CREATED",
    };
  } else if (a.includes("status")) {
    return {
      icon: "bi-arrow-repeat",
      iconClass: "atl-icon-status",
      badge: "atl-badge-updated",
      badgeText: "STATUS CHANGED",
    };
  } else if (a.includes("reply") || a.includes("sent") || a.includes("received")) {
    return {
      icon: "bi-envelope-fill",
      iconClass: "atl-icon-email",
      badge: "atl-badge-updated",
      badgeText: "EMAIL",
    };
  } else if (a.includes("deleted") || a.includes("removed")) {
    return {
      icon: "bi-trash-fill",
      iconClass: "atl-icon-deleted",
      badge: "atl-badge-deleted",
      badgeText: "DELETED",
    };
  } else if (a.includes("update") || a.includes("change")) {
    return {
      icon: "bi-pencil-square",
      iconClass: "atl-icon-info",
      badge: "atl-badge-updated",
      badgeText: "UPDATED",
    };
  } else {
    return {
      icon: "bi-circle-fill",
      iconClass: "atl-icon-default",
      badge: "atl-badge-updated",
      badgeText: "ACTIVITY",
    };
  }
};

  const getUserInitials = (name: string | null) => {
    if (!name || name === "N/A") return "S";
    const parts = name.split(/[@.\s]/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name: string | null) => {
    const colors = [
      "atl-avatar-green",
      "atl-avatar-blue",
      "atl-avatar-purple",
      "atl-avatar-gray",
    ];
    if (!name) return "atl-avatar-gray";
    return colors[name.length % colors.length];
  };

  const downloadCSV = () => {
    if (!emailAuditTrailData?.length) return;
    const headers = [
      "Date",
      "Time",
      "Activity",
      "Action Done By",
      "Assigned By",
      "Old Assignee",
      "New Assignee",
      "Previous Value",
      "New Value",
    ];
    const quote = (v: any) => {
      const s = String(v ?? "");
      return /[,"\s]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = emailAuditTrailData.map((r) =>
      [
        quote(formatDate(r.created_at)),
        quote(formatTime(r.created_at)),
        quote(r.email_activity || "N/A"),
        quote(r.action_done_by || "N/A"),
        quote(r.assigned_by || "N/A"),
        quote(r.old_assignee || "N/A"),
        quote(r.new_assignee || "N/A"),
        quote(r.previous_value || "N/A"),
        quote(r.new_value || "N/A"),
      ].join(","),
    );
    const csv = [headers.map(quote).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "email_audit_trail.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="audit-trail-timeline-wrapper">
      <div className="audit-trail-timeline-header">
        <h2 className="audit-trail-timeline-title">Email Audit Trail</h2>
        <div className="audit-trail-export-actions">
          <button className="audit-trail-export-button" onClick={downloadCSV}>
            <i className="bi bi-file-earmark-text text-white"></i>
            Export CSV
          </button>
        </div>
      </div>

      {emailAuditTrailData && emailAuditTrailData.length > 0 ? (
        <div className="audit-trail-timeline-items-container">
          {emailAuditTrailData.map((data, index) => {
            const activityType = getActivityType(data.email_activity);

            return (
              <div
                key={data.id ?? index}
                className="audit-trail-timeline-event"
              >
                <div
                  className={`audit-trail-timeline-event-icon ${activityType.iconClass}`}
                  title={data.email_activity}
                >
                  <i className={`bi ${activityType.icon}`}></i>
                </div>

                <div className="audit-trail-timeline-event-card">
                  <div className="audit-trail-event-card-header">
                    <div className="audit-trail-event-timestamp">
                      {formatDate(data.created_at)} •{" "}
                      {formatTime(data.created_at)}
                    </div>
                    <div
                      className={`audit-trail-event-status-badge ${activityType.badge}`}
                    >
                      {activityType.badgeText}
                    </div>
                  </div>

                  <div className="audit-trail-event-card-body">
                    {/* Activity */}
                    {data.email_activity && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">ACTIVITY</div>
                        <div className="audit-trail-field-value">
                          {data.email_activity}
                        </div>
                      </div>
                    )}

                    {/* Old Assignee */}
                    {data.old_assignee && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          PREVIOUS ASSIGNEE
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.old_assignee,
                            )}`}
                          >
                            {getUserInitials(data.old_assignee)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.old_assignee}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* New Assignee */}
                    {data.new_assignee && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          NEW ASSIGNEE
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.new_assignee,
                            )}`}
                          >
                            {getUserInitials(data.new_assignee)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.new_assignee}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Assigned By */}
                    {data.assigned_by && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          ASSIGNED BY
                        </div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.assigned_by,
                            )}`}
                          >
                            {getUserInitials(data.assigned_by)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.assigned_by}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Done By */}
                    {data.action_done_by && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">ACTION BY</div>
                        <div className="audit-trail-user-profile">
                          <div
                            className={`audit-trail-user-avatar-circle ${getAvatarColor(
                              data.action_done_by,
                            )}`}
                          >
                            {getUserInitials(data.action_done_by)}
                          </div>
                          <div className="audit-trail-user-display-name">
                            {data.action_done_by}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Previous Value */}
                    {data.previous_value && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">
                          PREVIOUS VALUE
                        </div>
                        <div className="audit-trail-field-value atl-value-muted">
                          {data.previous_value}
                        </div>
                      </div>
                    )}

                    {/* New Value */}
                    {data.new_value && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">NEW VALUE</div>
                        <div className="audit-trail-field-value">
                          {data.new_value}
                        </div>
                      </div>
                    )}

                    {/* Email To List */}
                    {data.email_to_list?.length > 0 && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">TO</div>
                        <div className="audit-trail-field-value">
                          {data.email_to_list.join(", ")}
                        </div>
                      </div>
                    )}

                    {/* Email From List */}
                    {data.email_from_list?.length > 0 && (
                      <div className="audit-trail-event-field">
                        <div className="audit-trail-field-label">FROM</div>
                        <div className="audit-trail-field-value">
                          {data.email_from_list.join(", ")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="audit-trail-timeline-no-data">
          <div className="audit-trail-empty-icon">
            <i className="bi bi-envelope-x"></i>
          </div>
          <div className="audit-trail-empty-heading">No Email Audit Trail</div>
          <div className="audit-trail-empty-description">
            There are no email audit trail entries to display.
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailAuditTrail;